/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect } from 'react';
import { Outlet, createBrowserRouter, RouterProvider } from 'react-router-dom';
import styled from 'styled-components/macro';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from 'react-query/queryClient';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';
import Colors from 'constants/colors';
import PageNotFound from 'pages/PageNotFound';
import Login from 'pages/auth/Login';
import { routes } from 'configs/routes.config';
import { pathsConfig } from 'configs/paths.config';
import vi from 'date-fns/locale/vi';
import { Box } from '@mui/material';
import WelcomeApp from 'pages/Welcome/WelcomeApp';
import MaintenanceScreen from 'pages/Maintenance/MaintenanceScreen';
import { AbilityProvider } from 'abilities/abilities';
import CustomLoading from './components/CustomLoading';

const LayoutRoot = React.lazy(() => import('pages/LayoutRoot'));

const Container = styled.div`
  width: 100%;
  background-color: #fafafa;
  height: 100vh;
`;

const fallbackComponent = (
  <Box className="loading-container">
    <CustomLoading />
  </Box>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      ...routes.map((route) => ({
        path: route.path,
        element: (
          <React.Suspense fallback={fallbackComponent}>
            {route.component}
          </React.Suspense>
        ),
      })),
      {
        path: 'admin/*',
        element: (
          <React.Suspense fallback={fallbackComponent}>
            <LayoutRoot />
          </React.Suspense>
        ),
      },
      {
        index: true,
        element: <Login />,
      },
      {
        path: pathsConfig.forgotpassword.path,
        element: pathsConfig.forgotpassword.component,
      },
      {
        path: pathsConfig.resetPassword.path,
        element: pathsConfig.resetPassword.component,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'welcome',
        element: <WelcomeApp />,
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
      {
        path: 'maintenance',
        element: <MaintenanceScreen />,
      },
    ],
  },
]);

function Layout() {
  return <Outlet />;
}

function App() {
  const theme = createTheme({
    palette: {
      primary: {
        main: Colors.primary,
      },
      secondary: {
        main: Colors.placeholder,
      },
      warning: {
        main: Colors.orange,
      },
      error: {
        main: Colors.red,
      },
    },
    typography: {
      fontFamily: 'Inter!important',
      color: Colors.mainText,
      fontSize: 13,
    },
    overrides: {
      MuiSvgIcon: {
        root: {
          fontSize: '1.5rem',
        },
      },
      MuiSelect: {
        root: {
          paddingTop: '10px',
          paddingBottom: '10px',
        },
      },
      MUIRichTextEditor: {
        root: {
          border: `1px solid ${Colors.line}`,
          borderRadius: '4px',
        },
        container: {
          marginTop: 0,
        },
        toolbar: {
          backgroundColor: Colors.backgroundRichEditor,
          padding: '10.5px 14px',
        },
        editor: {
          padding: '10.5px 14px',
          minHeight: '5rem',
        },
        editorContainer: {
          marginTop: 0,
        },
        placeHolder: {
          padding: '10.5px 14px',
          marginTop: 0,
        },
      },
      MuiPaper: {
        root: {
          boxShadow:
            '0px 1px 1px -1px rgb(0 0 0 / 8%), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12) !important',
        },
      },
    },
  });

  const [deviceTypeMobile, setDeviceTypeMobile] = useState(false);

  useEffect(() => {
    let hasTouchScreen = false;
    if ('maxTouchPoints' in navigator) {
      hasTouchScreen = navigator.maxTouchPoints > 0;
    } else if ('msMaxTouchPoints' in navigator) {
      hasTouchScreen = navigator.msMaxTouchPoints > 0;
    } else {
      const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
      if (mQ && mQ.media === '(pointer:coarse)') {
        hasTouchScreen = !!mQ.matches;
      } else if ('orientation' in window) {
        hasTouchScreen = true;
      } else {
        const UA = navigator.userAgent;
        hasTouchScreen = /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA)
          || /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
      }
    }
    if (hasTouchScreen) {
      setDeviceTypeMobile(true);
    } else {
      setDeviceTypeMobile(false);
    }
  }, [deviceTypeMobile]);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
      <QueryClientProvider client={queryClient}>
        <AbilityProvider>
          <Container className="app">
            {deviceTypeMobile ? (
              <WelcomeApp />
            ) : (
              <>
                <CssBaseline />
                <ThemeProvider theme={theme}>
                  <RouterProvider router={router} />
                </ThemeProvider>
              </>
            )}
          </Container>
        </AbilityProvider>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </LocalizationProvider>
  );
}

export default App;
